import React from "react";
import { titleWordFitScale } from "../../landingStudio/heroTextStyle";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ExploreIcon from "@mui/icons-material/Explore";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PlaceIcon from "@mui/icons-material/Place";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import StarIcon from "@mui/icons-material/Star";
import VerifiedIcon from "@mui/icons-material/Verified";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import { isSvgLandingAsset, resolveLandingAssetUrl } from "../../../../utils/landingAssets";
import LandingSegmentToolbar from "../../landingStudio/LandingSegmentToolbar";
import {
  SEGMENT_LABELS,
  normalizeImagePosition,
} from "../../landingStudio/segmentVisualStyle";
import { getSegmentDefinition } from "../../landingStudio/segmentRegistry";
import { resolveLandingPreviewResponsiveValue } from "../../landingStudio/previewDevices";
import { CATALOG_CARD_COLOR_FALLBACKS, resolveCatalogCardButtonColors, catalogCardPreviewShadow } from "../../landingStudio/catalogCardColors";

const HERO_CTA_ICON_COMPONENTS = Object.freeze({
  explore: ExploreIcon,
  storefront: StorefrontIcon,
  whatsapp: WhatsAppIcon,
  phone: PhoneIcon,
  mail: EmailIcon,
  place: PlaceIcon,
  shopping: ShoppingBagIcon,
  event: EventAvailableIcon,
  star: StarIcon,
  verified: VerifiedIcon,
  autoawesome: AutoAwesomeIcon,
  arrow: ArrowForwardIcon,
});

function HeroCanvasCtaIcon({ name = "", fallback = "explore" }) {
  const Icon = HERO_CTA_ICON_COMPONENTS[String(name || "").trim()] || HERO_CTA_ICON_COMPONENTS[fallback];
  return Icon ? <Icon /> : null;
}

const HIGHLIGHT_ICON_COMPONENTS = Object.freeze({
  star: StarRoundedIcon,
  support: SupportAgentRoundedIcon,
  verified: VerifiedRoundedIcon,
  speed: BoltRoundedIcon,
  quality: WorkspacePremiumRoundedIcon,
  local: StorefrontRoundedIcon,
  security: ShieldRoundedIcon,
  autoawesome: AutoAwesomeRoundedIcon,
  event: EventAvailableRoundedIcon,
  groups: GroupsRoundedIcon,
  photo: PhotoCameraRoundedIcon,
  design: DesignServicesRoundedIcon,
});

function HighlightCanvasIcon({ name = "", ...props }) {
  const Icon = HIGHLIGHT_ICON_COMPONENTS[String(name || "").trim()];
  if (!Icon) return null;
  return <Icon {...props} />;
}

function supportsLogoMask() {
  return typeof CSS !== "undefined" && (
    CSS.supports?.("mask-image", 'url("")') ||
    CSS.supports?.("-webkit-mask-image", 'url("")')
  );
}

function cssMaskUrl(value) {
  const safe = String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/[\n\r\f]/g, "");
  return `url("${safe}")`;
}

function SafeCanvasImage({
  src,
  alt = "",
  sx,
  onEdit,
  editLabel = "Editar imagen",
  landingField = "",
  landingElementId = "",
  landingElementType = "image",
  monochromeColor = "",
}) {
  const [broken, setBroken] = React.useState(false);

  React.useEffect(() => {
    setBroken(false);
  }, [src]);

  const hasSource = Boolean(String(src || "").trim());
  if (!hasSource) return null;

  const contextualTarget = Boolean(landingField || landingElementId);
  const editableTarget = Boolean(onEdit || contextualTarget);
  const activeMonochrome = Boolean(
    String(monochromeColor || "").trim() &&
    isSvgLandingAsset(src) &&
    supportsLogoMask()
  );
  const handleKeyDown = (event) => {
    if (!onEdit || (event.key !== "Enter" && event.key !== " ")) return;
    event.preventDefault();
    onEdit(event);
  };
  const targetProps = {
    "data-landing-field": landingField || undefined,
    "data-landing-element-id": landingElementId || undefined,
    "data-landing-element-type": contextualTarget ? landingElementType : undefined,
    "data-landing-edit-label": contextualTarget ? editLabel : undefined,
    role: editableTarget ? "button" : undefined,
    tabIndex: editableTarget ? 0 : undefined,
    title: editableTarget
      ? (broken ? `No se pudo cargar la imagen. ${editLabel}` : editLabel)
      : undefined,
    "aria-label": editableTarget
      ? (broken ? `No se pudo cargar la imagen. ${editLabel}` : editLabel)
      : undefined,
    onClick: onEdit,
    onKeyDown: handleKeyDown,
  };
  const interactiveSx = editableTarget ? {
    cursor: "pointer",
    outline: "2px solid transparent",
    outlineOffset: -2,
    transition: "outline-color .15s ease, filter .15s ease",
    "&:hover": {
      outlineColor: "rgba(243,90,58,.92)",
      filter: "brightness(.96)",
    },
    "&:focus-visible": {
      outlineColor: "rgba(243,90,58,1)",
    },
  } : {};

  if (broken) {
    if (!editableTarget) return null;

    return (
      <Box
        {...targetProps}
        sx={{
          ...sx,
          ...interactiveSx,
          display: "grid",
          placeItems: "start end",
          overflow: "hidden",
          bgcolor: "rgba(15,23,42,.14)",
          backgroundImage:
            "repeating-linear-gradient(135deg, rgba(255,255,255,.08) 0, rgba(255,255,255,.08) 8px, rgba(15,23,42,.05) 8px, rgba(15,23,42,.05) 16px)",
        }}
      >
        <Stack
          direction="row"
          spacing={0.7}
          alignItems="center"
          aria-hidden="true"
          sx={{
            m: 1,
            maxWidth: "calc(100% - 16px)",
            px: 1,
            py: 0.7,
            borderRadius: 1.5,
            color: "#fff",
            bgcolor: "rgba(15,23,42,.88)",
            boxShadow: "0 4px 16px rgba(15,23,42,.24)",
            pointerEvents: "none",
          }}
        >
          <PhotoCameraRoundedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ display: "block", fontWeight: 900, lineHeight: 1.15 }}
            >
              No se pudo cargar la imagen
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.2,
                opacity: 0.82,
                lineHeight: 1.1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {editLabel}
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  }

  if (!activeMonochrome) {
    return (
      <Box
        component="img"
        src={src}
        alt={alt}
        {...targetProps}
        onError={() => setBroken(true)}
        sx={{ ...sx, ...interactiveSx }}
      />
    );
  }

  return (
    <Box
      {...targetProps}
      sx={{
        display: "inline-grid",
        maxWidth: "100%",
        verticalAlign: "middle",
        ...interactiveSx,
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        onError={() => setBroken(true)}
        sx={{ ...sx, gridArea: "1 / 1", visibility: "hidden", pointerEvents: "none" }}
      />
      <Box
        aria-hidden="true"
        sx={{
          gridArea: "1 / 1",
          width: "100%",
          height: "100%",
          bgcolor: String(monochromeColor).trim(),
          WebkitMaskImage: cssMaskUrl(src),
          maskImage: cssMaskUrl(src),
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskSize: "contain",
          maskSize: "contain",
          WebkitMaskPosition: sx?.objectPosition || "center",
          maskPosition: sx?.objectPosition || "center",
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}

const LANDING_CANVAS_RENDER_DEFAULTS = Object.freeze({
  typography: Object.freeze({
    baseFontSize: 16,
    scaleRatio: 1.2,
    lineHeight: 1.5,
    headingLineHeight: 1.15,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 800,
    letterSpacing: 0,
  }),
  spacing: Object.freeze({
    unit: 8,
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
    section: 72,
  }),
  borders: Object.freeze({
    width: 1,
    style: "solid",
    radiusSm: 5,
    radiusMd: 10,
    radiusLg: 18,
    radiusPill: 9999,
  }),
  shadows: Object.freeze({
    sm: "0 1px 3px rgba(26,26,46,.10)",
    md: "0 8px 24px rgba(26,26,46,.12)",
    lg: "0 18px 48px rgba(26,26,46,.16)",
    focus: "0 0 0 3px rgba(243,90,58,.24)",
  }),
});

function finite(value, fallback) {
  if (value === "" || value === null || value === undefined) return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function px(value, fallback = 0) {
  return `${finite(value, fallback)}px`;
}

function normalizeHeroHorizontalPosition(value) {
  const candidate = String(value || "").trim().toLowerCase();
  if (["right", "derecha", "end", "flex-end"].includes(candidate)) return "right";
  if (["center", "centre", "centro"].includes(candidate)) return "center";
  return "left";
}

function normalizeHeroVerticalPosition(value) {
  const candidate = String(value || "").trim().toLowerCase();
  if (["top", "arriba", "start", "flex-start"].includes(candidate)) return "top";
  if (["bottom", "abajo", "end", "flex-end"].includes(candidate)) return "bottom";
  return "center";
}

function heroHorizontalFlex(value) {
  if (value === "right") return "flex-end";
  if (value === "center") return "center";
  return "flex-start";
}

function heroVerticalFlex(value) {
  if (value === "top") return "flex-start";
  if (value === "bottom") return "flex-end";
  return "center";
}

function colorWithAlpha(value, alphaHex) {
  const candidate = String(value || "").trim();
  if (!candidate || candidate.toLowerCase() === "transparent") return "transparent";
  if (/^#[0-9a-f]{6}$/i.test(candidate)) return `${candidate}${alphaHex}`;
  if (/^#[0-9a-f]{3}$/i.test(candidate)) {
    const expanded = `#${candidate.slice(1).split("").map((char) => `${char}${char}`).join("")}`;
    return `${expanded}${alphaHex}`;
  }
  return candidate;
}

function SectionFrame({
  sectionId,
  sectionType = sectionId,
  sectionLabel = "",
  activeSection,
  studioMode,
  onSelectSection,
  onSelectElement,
  selectedElement,
  elementSelectionRequiresActive = false,
  captureBackgroundSelection = false,
  surfaceFieldPath = "",
  surfaceEditLabel = "Editar fondo de la sección",
  onOpenSegmentTool,
  onToggleSection,
  onRemoveSection,
  device,
  children,
  sx,
  backgroundSx,
  layoutOrder,
}) {
  const active = activeSection === sectionId;
  const label = sectionLabel || SEGMENT_LABELS[sectionType] || SEGMENT_LABELS[sectionId] || sectionId;
  const definition = getSegmentDefinition(sectionType);
  const canToggle = sectionType === "custom" || Boolean(definition?.visibilityPath);
  const canRemove = Boolean(onRemoveSection) && !["navbar", "footer"].includes(sectionType);
  const sectionRef = React.useRef(null);
  const select = () => onSelectSection?.(sectionId);

  const selectionKey = selectedElement?.sectionId === sectionId
    ? (selectedElement?.elementId ? `@${selectedElement.elementId}` : String(selectedElement?.fieldPath || ""))
    : "";

  React.useEffect(() => {
    if (!studioMode || !sectionRef.current) return;
    const targets = sectionRef.current.querySelectorAll('[data-landing-field], [data-landing-element-id]');
    targets.forEach((node) => {
      const fieldPath = node.getAttribute("data-landing-field") || "";
      const elementId = node.getAttribute("data-landing-element-id") || "";
      const key = elementId ? `@${elementId}` : fieldPath;
      const editLabel = node.getAttribute("data-landing-edit-label") || "Editar elemento";
      if (!node.matches('button,a,input,select,textarea,[tabindex]')) node.setAttribute("tabindex", "0");
      if (!node.getAttribute("role") && !node.matches('button,a,input,select,textarea')) node.setAttribute("role", "button");
      if (!node.getAttribute("aria-label")) node.setAttribute("aria-label", editLabel);
      if (!node.getAttribute("title")) node.setAttribute("title", editLabel);
      if (key && key === selectionKey) node.setAttribute("data-landing-element-selected", "true");
      else node.removeAttribute("data-landing-element-selected");
    });
  });

  const activateTarget = (event) => {
    if (!studioMode || !onSelectElement) return false;
    if (elementSelectionRequiresActive && !active) return false;
    const target = event.target?.closest?.('[data-landing-field], [data-landing-element-id]');
    if (!target || !sectionRef.current?.contains(target)) return false;
    const fieldPath = target.getAttribute("data-landing-field") || "";
    const elementId = target.getAttribute("data-landing-element-id") || "";
    if (!fieldPath && !elementId) return false;
    event.preventDefault?.();
    event.stopPropagation?.();
    onSelectSection?.(sectionId);
    onSelectElement({
      sectionId,
      sectionType,
      fieldPath,
      elementId,
      logicalType: target.getAttribute("data-landing-element-type") || "field",
      previewDevice: device || "desktop",
    });
    return true;
  };

  const handleBackgroundClickCapture = (event) => {
    if (!studioMode || !captureBackgroundSelection) return;
    const target = event.target?.closest?.('[data-landing-field], [data-landing-element-id]');
    const isChildEditableTarget = target && target !== sectionRef.current && sectionRef.current?.contains(target);
    if (isChildEditableTarget) return;
    // Resolve the explicit section surface during capture so nested layout layers cannot
    // cancel background editing by stopping the bubbling phase.
    if (surfaceFieldPath && activateTarget(event)) return;
    select();
  };

  const handleClick = (event) => {
    if (activateTarget(event)) return;
    select();
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    if (event.target === event.currentTarget) {
      if (surfaceFieldPath && activateTarget(event)) return;
      event.preventDefault();
      select();
      return;
    }
    activateTarget(event);
  };

  return (
    <Box data-landing-section-frame={sectionId} sx={Number.isFinite(layoutOrder) ? { order: layoutOrder } : undefined}>
      {studioMode && active ? (
        <LandingSegmentToolbar
          sectionId={sectionId}
          label={label}
          enabled
          device={device}
          contextual={sectionType === "custom" || definition?.elementEditingReady === true}
          canToggle={canToggle}
          canRemove={canRemove}
          onToggle={(nextVisible) => onToggleSection?.(sectionId, nextVisible)}
          onRemove={() => onRemoveSection?.(sectionId)}
          onOpenTool={onOpenSegmentTool}
        />
      ) : null}

      <Box
        ref={sectionRef}
        component="section"
        data-landing-section={sectionId}
        data-landing-section-active={active ? "true" : undefined}
        data-landing-field={studioMode && surfaceFieldPath ? surfaceFieldPath : undefined}
        data-landing-element-type={studioMode && surfaceFieldPath ? "visual" : undefined}
        data-landing-edit-label={studioMode && surfaceFieldPath ? surfaceEditLabel : undefined}
        tabIndex={studioMode ? 0 : undefined}
        aria-label={studioMode ? (surfaceFieldPath ? `${label}. ${surfaceEditLabel}.` : `${label}. Activar para editar esta sección.`) : undefined}
        title={studioMode && surfaceFieldPath ? surfaceEditLabel : undefined}
        onClickCapture={captureBackgroundSelection ? handleBackgroundClickCapture : undefined}
        onClick={handleClick}
        onKeyDown={studioMode ? handleKeyDown : undefined}
        sx={{
          position: "relative",
          scrollMarginBlock: "72px",
          cursor: studioMode ? "pointer" : "default",
          touchAction: studioMode ? "manipulation" : undefined,
          WebkitTapHighlightColor: studioMode ? "transparent" : undefined,
          outline: active ? "3px solid rgba(243,90,58,.88)" : "2px solid transparent",
          outlineOffset: -2,
          boxShadow: active ? "inset 0 0 0 1px rgba(255,255,255,.72)" : undefined,
          transition: "outline-color .15s ease, box-shadow .15s ease",
          "&:hover": studioMode ? { outlineColor: "rgba(243,90,58,.48)" } : undefined,
          "&:focus-visible": studioMode ? {
            outlineColor: "rgba(243,90,58,1)",
            boxShadow: active
              ? "inset 0 0 0 1px rgba(255,255,255,.72), 0 0 0 4px rgba(243,90,58,.16)"
              : "0 0 0 4px rgba(243,90,58,.16)",
          } : undefined,
          ...(studioMode ? {
            "& [data-landing-field], & [data-landing-element-id]": {
              outline: "2px solid transparent",
              outlineOffset: 2,
              transition: "outline-color .14s ease, box-shadow .14s ease",
            },
            "& [data-landing-field]:hover, & [data-landing-element-id]:hover": {
              outlineColor: "rgba(243,90,58,.62)",
            },
            "& [data-landing-field]:focus-visible, & [data-landing-element-id]:focus-visible, & [data-landing-element-selected=\"true\"]": {
              outlineColor: "rgba(243,90,58,1)",
              boxShadow: "0 0 0 4px rgba(243,90,58,.14)",
            },
          } : {}),
          ...sx,
          ...backgroundSx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function cardBackgroundWithOpacity(value, opacity, fallback) {
  const candidate = String(value || fallback || "#ffffff").trim() || "#ffffff";
  const lowered = candidate.toLowerCase();
  const hasExplicitAlpha =
    lowered === "transparent" ||
    /^#[0-9a-f]{4}$/i.test(candidate) ||
    /^#[0-9a-f]{8}$/i.test(candidate) ||
    /^rgba\(/i.test(candidate) ||
    /^hsla\(/i.test(candidate);
  if (hasExplicitAlpha) return candidate;
  const alpha = Math.max(0, Math.min(1, finite(opacity, 1)));
  if (alpha <= 0) return "transparent";
  if (alpha >= 0.999) return candidate;
  return `color-mix(in srgb, ${candidate} ${Math.round(alpha * 1000) / 10}%, transparent)`;
}

function LandingPreviewHorizontalTrack({ children, mt = 0, gap = 0, ariaLabel = "", sx = {} }) {
  return (
    <Box
      role={ariaLabel ? "region" : undefined}
      aria-label={ariaLabel || undefined}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        mt,
        overflowX: "auto",
        overflowY: "hidden",
        overscrollBehaviorX: "contain",
        WebkitOverflowScrolling: "touch",
        scrollSnapType: "x proximity",
        scrollbarWidth: "thin",
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          width: "max-content",
          maxWidth: "none",
          minWidth: "100%",
          gap,
          pr: 0.5,
          "& > *": { scrollSnapAlign: "start" },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function PlaceholderCards({
  labels,
  primary,
  surface,
  renderDefaults,
  device,
  cardConfig = {},
  imageScale = 1,
  spacingScale = 1,
  landingElementId = "",
}) {
  const showImage = cardConfig.showImage !== false;
  const showTitle = cardConfig.showTitle !== false;
  const showDuration = cardConfig.showDuration !== false;
  const showCapabilities = cardConfig.showCapabilities !== false;
  const imagePosition = ["top", "left", "right"].includes(cardConfig.imagePosition) ? cardConfig.imagePosition : "top";
  const horizontalImage = showImage && imagePosition !== "top" && device !== "mobile";
  const cardBackgroundImage = resolveLandingAssetUrl(cardConfig.backgroundImageUrl);
  const backgroundImagePosition = normalizeImagePosition(cardConfig.backgroundImagePosition);
  const backgroundImageFit = cardConfig.backgroundImageFit === "contain" ? "contain" : "cover";
  const cardRadius = finite(cardConfig.radius, renderDefaults.borders.radiusMd);
  const cardGap = finite(cardConfig.gap, 18);
  const cardImageHeight = finite(cardConfig.imageHeight, 210) * imageScale;
  const showType = cardConfig.showType !== false;
  const showCategory = cardConfig.showCategory !== false;
  const showSubcategory = cardConfig.showSubcategory !== false;
  const showDescription = cardConfig.showDescription !== false;
  const showPrice = cardConfig.showPrice !== false;
  const showButton = cardConfig.showButton !== false;
  // Dueño único: catalog.cards.backgroundColor. Los antiguos elementColors
  // sólo alteraban el canvas, nunca la web: no deben prevalecer aquí.
  const cardSurfaceBackground = cardBackgroundWithOpacity(cardConfig.backgroundColor, cardConfig.backgroundOpacity, CATALOG_CARD_COLOR_FALLBACKS.surface);
  const buttonColors = resolveCatalogCardButtonColors(cardConfig);

  const cardWidth = resolveLandingPreviewResponsiveValue(
    device,
    { xs: 230, sm: 252, md: 280 },
    230
  );

  return (
    <LandingPreviewHorizontalTrack
      mt={2}
      gap={`${cardGap}px`}
      ariaLabel="Vista previa horizontal de tarjetas destacadas"
    >
      {labels.map((label, index) => (
        <Paper
          key={`${label}-${index}`}
          elevation={0}
          data-landing-element-id={landingElementId || undefined}
          data-landing-element-type={landingElementId ? "complex" : undefined}
          data-landing-edit-label={landingElementId ? "Editar tarjetas del catálogo" : undefined}
          data-landing-field="catalog.cards.surface"
          sx={{
            position: "relative",
            flex: "0 0 auto",
            width: `${cardWidth}px`,
            minWidth: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: horizontalImage ? (imagePosition === "right" ? "row-reverse" : "row") : "column",
            borderRadius: `${cardRadius}px`,
            bgcolor: cardSurfaceBackground,
            border: cardConfig.borderColor
              ? `1px solid ${cardConfig.borderColor}`
              : `${finite(renderDefaults.borders.width, 1)}px ${renderDefaults.borders.style || "solid"} ${renderDefaults.borders.color || renderDefaults.colors.divider}`,
            boxShadow: catalogCardPreviewShadow(cardConfig, renderDefaults.shadows.md || renderDefaults.shadows.sm),
            isolation: "isolate",
            "&::before": cardBackgroundImage ? {
              content: '""',
              position: "absolute",
              inset: 0,
              zIndex: 0,
              pointerEvents: "none",
              backgroundImage: `url("${String(cardBackgroundImage).replace(/"/g, "%22")}")`,
              backgroundSize: backgroundImageFit,
              backgroundPosition: backgroundImagePosition,
              backgroundRepeat: "no-repeat",
              opacity: Math.max(0, Math.min(1, finite(cardConfig.backgroundImageOpacity, 0.28))),
            } : undefined,
            "& > *": { position: "relative", zIndex: 1 },
          }}
        >
          {showImage ? (
            <Box
              sx={{
                width: horizontalImage ? "38%" : "100%",
                minWidth: horizontalImage ? 72 : 0,
                height: horizontalImage ? "auto" : `${cardImageHeight}px`,
                minHeight: horizontalImage ? `${Math.min(cardImageHeight, 150)}px` : 0,
                bgcolor: colorWithAlpha(primary, "18"),
                background: `linear-gradient(135deg, ${colorWithAlpha(primary, "22")}, ${colorWithAlpha(renderDefaults.colors.secondary, "18")})`,
                flexShrink: 0,
              }}
            />
          ) : null}
          <Stack sx={{ p: px(finite(renderDefaults.spacing.md, 16) * spacingScale, 16), minWidth: 0, flex: 1 }} spacing={0.45}>
            {showType ? <Typography data-landing-field="catalog.cards.typeColor" variant="caption" sx={{ color: cardConfig.typeColor || CATALOG_CARD_COLOR_FALLBACKS.type, fontWeight: 800 }}>{index % 2 ? "Servicio" : "Producto"}</Typography> : null}
            {showTitle ? <Typography data-landing-field="catalog.cards.titleColor" sx={{ color: cardConfig.titleColor || CATALOG_CARD_COLOR_FALLBACKS.title, fontSize: `${0.88 * titleWordFitScale(label)}rem`, overflowWrap: "normal", wordBreak: "normal", hyphens: "none", fontWeight: finite(renderDefaults.typography.fontWeightBold, 800) }}>{label}</Typography> : null}
            {showCategory ? <Typography data-landing-field="catalog.cards.categoryColor" variant="caption" sx={{ color: cardConfig.categoryColor || CATALOG_CARD_COLOR_FALLBACKS.category }}>Categoría ejemplo</Typography> : null}
            {showSubcategory ? <Typography data-landing-field="catalog.cards.subcategoryColor" variant="caption" sx={{ color: cardConfig.subcategoryColor || CATALOG_CARD_COLOR_FALLBACKS.subcategory }}>Subcategoría</Typography> : null}
            {showDescription ? <Typography data-landing-field="catalog.cards.descriptionColor" variant="caption" sx={{ color: cardConfig.descriptionColor || CATALOG_CARD_COLOR_FALLBACKS.description }}>Descripción visible según la configuración.</Typography> : null}
            {showDuration && index % 2 ? <Typography data-landing-field="catalog.cards.descriptionColor" variant="caption" sx={{ color: cardConfig.descriptionColor || CATALOG_CARD_COLOR_FALLBACKS.description }}>Duración: 60 min</Typography> : null}
            {showCapabilities && index % 2 ? (
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                <Chip size="small" label="Reserva online" />
                <Chip size="small" label="Entrega" />
              </Stack>
            ) : null}
            {showPrice ? <Typography data-landing-field="catalog.cards.priceColor" variant="body2" sx={{ mt: 0.35, fontWeight: 900, color: cardConfig.priceColor || CATALOG_CARD_COLOR_FALLBACKS.price }}>$ 12.500</Typography> : null}
            {showButton ? (
              <Button
                data-landing-field={index % 2 ? "catalog.cards.serviceCtaLabel" : "catalog.cards.ctaLabel"}
                size="small"
                variant="contained"
                sx={{
                  alignSelf: "flex-start",
                  mt: 0.7,
                  textTransform: "none",
                  // Preview fiel a Productos destacados y /productos.
                  color: buttonColors.text,
                  bgcolor: buttonColors.background,
                  "&:hover": {
                    color: buttonColors.hoverText,
                    bgcolor: buttonColors.hoverBackground,
                  },
                }}
              >
                {index % 2 ? (cardConfig.serviceCtaLabel || "Reservar") : (cardConfig.ctaLabel || "Ver detalle")}
              </Button>
            ) : null}
          </Stack>
        </Paper>
      ))}
    </LandingPreviewHorizontalTrack>
  );
}

export {
  HeroCanvasCtaIcon,
  HighlightCanvasIcon,
  SafeCanvasImage,
  LANDING_CANVAS_RENDER_DEFAULTS,
  finite,
  px,
  normalizeHeroHorizontalPosition,
  normalizeHeroVerticalPosition,
  heroHorizontalFlex,
  heroVerticalFlex,
  colorWithAlpha,
  SectionFrame,
  LandingPreviewHorizontalTrack,
  PlaceholderCards
};
